import { Component, Input, OnDestroy, AfterViewInit, ElementRef, ViewChild, NgZone, inject } from '@angular/core';
import { Chart } from 'chart.js';
// import { Chart, LineController, BarController, LineElement, BarElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler } from 'chart.js';
// import ChartDataLabels from 'chartjs-plugin-datalabels';
import { isTimeCol, isNumericType, isCategory, isKpiCol, formatDateLabel } from '../colimind-utils';
import type { QueryResult, ChartMode } from '../colimind-types';

// #586938 - Fix global chart register for colimind demo charts
// this must not be registered globally for chart js as it screws the others charts
// only register locally for each chart which might be used in the colimind context
// the global chart register is done in src/app/app.component.ts
// Chart.register(LineController, BarController, LineElement, BarElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler, ChartDataLabels);

const COLORS = ['#2272a5', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#003067', '#ec4899', '#14b8a6'];

const TICK_STYLE = { font: { size: 11 }, color: '#9ca3af' };
const X_SCALE = { grid: { display: false }, ticks: TICK_STYLE };
const Y_SCALE = { grid: { color: '#f3f4f6' }, ticks: TICK_STYLE };

function pctCallback(v: number): string {
  return v + ' %';
}
function pctLabel(ctx: { parsed: { y: number } }): string {
  return ctx.parsed.y.toFixed(1) + ' %';
}

@Component({
  selector: 'colimo-chart-widget',
  standalone: true,
  template: `<div class="colimind-chart"><canvas #canvas></canvas></div>`,
  styles: [`.colimind-chart { position: relative; height: 220px; }`],
})
export class ChartWidgetComponent implements AfterViewInit, OnDestroy {
  @Input() queryResult!: QueryResult;
  @Input() mode: ChartMode = 'line_chart';
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  private chart?: Chart;
  private zone = inject(NgZone);

  ngAfterViewInit(): void { this.zone.runOutsideAngular(() => this.buildChart()); }

  private buildChart(): void {
    const cols = this.queryResult.columns;
    const rows = this.queryResult.data_array as string[][];
    if (this.mode === 'line_chart' || this.mode === 'multi_line_chart') this.buildLineChart(cols, rows);
    else if (this.mode === 'bar_chart') this.buildSimpleBarChart(cols, rows);
    else if (this.mode === 'stacked_bar_chart') this.buildStackedBarChart(cols, rows);
  }

  private buildLineChart(cols: QueryResult['columns'], rows: string[][]): void {
    const timeIdx = cols.findIndex(isTimeCol);
    const numIdxs = cols.map((c, i) => (isNumericType(c) ? i : -1)).filter(i => i >= 0);
    if (timeIdx < 0 || numIdxs.length === 0) return;

    const labels = rows.map(r => formatDateLabel(r[timeIdx]));
    const datasets = numIdxs.map((ci, di) => {
      const col = cols[ci];
      const pct = isKpiCol(col.name);
      return {
        label: col.name.replace(/_/g, ' '),
        data: rows.map(r => {
          const v = parseFloat(r[ci]);
          return isNaN(v) ? null : pct ? Number((v * 100).toFixed(1)) : Number(v.toFixed(3));
        }),
        borderColor: COLORS[di % COLORS.length],
        backgroundColor: di === 0 ? 'rgba(34,114,165,0.07)' : 'transparent',
        borderWidth: 2, pointRadius: rows.length > 30 ? 0 : 3, tension: 0.3, fill: di === 0,
      };
    });

    const pct = isKpiCol(cols[numIdxs[0]].name);
    this.chart = new Chart(this.canvasRef.nativeElement, {
      type: 'line', data: { labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: datasets.length > 1, position: 'top', labels: { boxWidth: 12, font: { size: 11 } } },
          datalabels: { display: false },
          tooltip: { callbacks: { label: pct ? pctLabel : undefined } },
        },
        scales: {
          x: { ...X_SCALE, ticks: { ...TICK_STYLE, maxRotation: 45 } },
          y: { ...Y_SCALE, ticks: { ...TICK_STYLE, callback: pct ? pctCallback : undefined } },
        },
      },
    });
  }

  private buildSimpleBarChart(cols: QueryResult['columns'], rows: string[][]): void {
    const catIdxs = cols.map((c, i) => (isCategory(c) ? i : -1)).filter(i => i >= 0);
    const numIdx = cols.findIndex(isNumericType);
    if (catIdxs.length === 0 || numIdx < 0) return;

    const numCol = cols[numIdx];
    const pct = isKpiCol(numCol.name);
    const labels = rows.map(r => r[catIdxs[0]]);
    const values = rows.map(r => {
      const v = parseFloat(r[numIdx]);
      return isNaN(v) ? 0 : pct ? Number((v * 100).toFixed(1)) : Number(v.toFixed(3));
    });

    this.chart = new Chart(this.canvasRef.nativeElement, {
      type: 'bar',
      data: { labels, datasets: [{ label: numCol.name.replace(/_/g, ' '), data: values, backgroundColor: COLORS[0] + 'cc', borderColor: COLORS[0], borderWidth: 1, borderRadius: 4 }] },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false }, datalabels: { display: false }, tooltip: { callbacks: { label: pct ? pctLabel : undefined } } },
        scales: { x: X_SCALE, y: { ...Y_SCALE, ticks: { ...TICK_STYLE, callback: pct ? pctCallback : undefined } } },
      },
    });
  }

  private buildStackedBarChart(cols: QueryResult['columns'], rows: string[][]): void {
    const catIdxs = cols.map((c, i) => (isCategory(c) ? i : -1)).filter(i => i >= 0);
    const numIdx = cols.findIndex(isNumericType);
    if (catIdxs.length < 2 || numIdx < 0) return;

    const [cat1, cat2] = catIdxs;
    const pct = isKpiCol(cols[numIdx].name);
    const labels = [...new Set(rows.map(r => r[cat1]))];
    const stacks = [...new Set(rows.map(r => r[cat2]))];
    const datasets = stacks.map((stack, si) => ({
      label: stack,
      data: labels.map(lbl => {
        const row = rows.find(r => r[cat1] === lbl && r[cat2] === stack);
        if (!row) return 0;
        const v = parseFloat(row[numIdx]);
        return isNaN(v) ? 0 : pct ? Number((v * 100).toFixed(1)) : Number(v.toFixed(3));
      }),
      backgroundColor: COLORS[si % COLORS.length] + 'cc', borderRadius: 4,
    }));

    this.chart = new Chart(this.canvasRef.nativeElement, {
      type: 'bar', data: { labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: true, position: 'top', labels: { boxWidth: 12, font: { size: 11 } } }, datalabels: { display: false } },
        scales: { x: { stacked: true, ...X_SCALE }, y: { stacked: true, ...Y_SCALE, ticks: TICK_STYLE } },
      },
    });
  }

  ngOnDestroy(): void { this.chart?.destroy(); }
}
